<template>
  <div class="container-fluid">
    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Pedidos de la tienda online</h1>
          <p class="view-header__subtitle">Gestioná los pedidos recibidos desde el e-commerce y su estado.</p>
        </div>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="filters-panel">
      <div class="filters-panel__section">
        <div class="filters-panel__section-title">
          <i class="bi bi-search"></i>
          Buscador
        </div>
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <input v-model="filters.q" type="search" class="form-control" placeholder="Buscar...">
          </div>
          <div class="filters-panel__field">
            <div class="form-check">
              <input id="oninvoiced" v-model="filters.oninvoiced" class="form-check-input" type="checkbox">
              <label class="form-check-label" for="oninvoiced">Mostrar pedidos terminados</label>
            </div>
          </div>
          <div class="filters-panel__field filters-panel__field--inline">
            <label class="control-label">Fecha:</label>
            <Daterange ref="daterange" :opendir="'left'" :initial-start="dateRangeStart" :initial-end="dateRangeEnd" @datepicked="onDatePicked" />
          </div>
          <div class="filters-panel__actions">
            <button type="button" class="filters-panel__btn filters-panel__btn--outline" @click="clearFilters">
              <i class="bi bi-eraser"></i>
              Limpiar filtros
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- TABLA -->
    <div class="data-table">
      <div class="table-responsive">
        <table class="table mb-0">
          <thead>
            <tr role="row">
              <th>Fecha</th>
              <th>N°</th>
              <th>Titular</th>
              <th>E-mail</th>
              <th class="text-right">Total</th>
              <th>Pago</th>
              <th>Estado</th>
              <th class="text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="orders.length < 1">
              <td colspan="8" class="data-table__empty">No hay registros disponibles</td>
            </tr>
            <tr v-for="order in orders" :key="order.id" role="row">
              <td>{{ order.created_at }}</td>
              <td><span class="data-table__primary">{{ order.number }}</span></td>
              <td><span class="data-table__primary">{{ order.client_name }}</span></td>
              <td class="data-table__cell-soft">{{ order.client_email }}</td>
              <td class="text-right"><span class="data-table__primary">{{ order.total }}</span></td>
              <td class="data-table__cell-soft">{{ order.paymethod }}</td>
              <td>
                <div
                  v-if="order.status != 'CANCELED'"
                  class="dropdown"
                  :class="{ show: openOrderId === order.id }"
                >
                  <button
                    type="button"
                    class="status-badge-toggle p-0 border-0 shadow-none"
                    :aria-expanded="openOrderId === order.id ? 'true' : 'false'"
                    @click.stop="toggleDropdown(order.id, $event)"
                  >
                    <span
                      class="status-badge"
                      :class="{
                        'status-badge--warning': order.status == 'PENDING',
                        'status-badge--accent': order.status == 'MAKING',
                        'status-badge--success': order.status == 'READY'
                      }"
                    >
                      <template v-if="order.status == 'PENDING'">Pendiente</template>
                      <template v-if="order.status == 'MAKING'">Preparando</template>
                      <template v-if="order.status == 'READY'">Terminada</template>
                      <i class="bi bi-chevron-down"></i>
                    </span>
                  </button>
                  <div
                    class="dropdown-menu dropdown-menu-right"
                    :class="{ show: openOrderId === order.id }"
                    :style="openOrderId === order.id ? dropdownStyle : {}"
                  >
                    <a class="dropdown-item" :class="{ active: order.status == 'PENDING' }" href="#" @click.prevent="setStatus(order.id, 'PENDING'); openOrderId = null;">Pendiente</a>
                    <a class="dropdown-item" :class="{ active: order.status == 'MAKING' }" href="#" @click.prevent="setStatus(order.id, 'MAKING'); openOrderId = null;">Preparando</a>
                    <a class="dropdown-item" :class="{ active: order.status == 'READY' }" href="#" @click.prevent="setStatus(order.id, 'READY'); openOrderId = null;">Terminada</a>
                    <div class="dropdown-divider"></div>
                    <a class="dropdown-item" href="#" @click.prevent="setStatus(order.id, 'CANCELED'); openOrderId = null;">Anulada</a>
                  </div>
                </div>
                <span v-else class="status-badge status-badge--danger">Anulada</span>
              </td>
              <td class="text-right">
                <div class="table-action-group">
                  <button
                    type="button"
                    class="table-action-btn"
                    title="Ver detalle"
                    @click="seeDetail(order.id)"
                  >
                    <i class="bi bi-file-earmark-text"></i>
                  </button>
                  <button
                    v-if="order.status == 'CANCELED'"
                    type="button"
                    class="table-action-btn"
                    title="Eliminar nota"
                    @click="deleteOrder(order.id)"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- PAGINACION -->
      <div class="data-table__footer">
        <div class="data-table__info">
          Mostrando {{ (filters.page - 1) * filters.itemsPerPage + 1 }} a {{
            (Math.min(filters.page * filters.itemsPerPage) > orders.length)
              ? totalItems
              : Math.min(filters.page * filters.itemsPerPage)
          }} de {{ totalItems }} registros
        </div>
        <ul class="data-table__pagination">
          <li class="page-item" :class="{ 'disabled': filters.page == 1 }">
            <a class="page-link" href="#" @click.prevent="prevPage()">Anterior</a>
          </li>
          <li v-if="(filters.page - (pageInterval / 2)) > 0" class="page-item">
            <a class="page-link" href="#" @click.prevent="prevPage()">...</a>
          </li>
          <template v-for="index in totalPages">
            <li
              v-if="inIntervalPage(index)"
              :key="index"
              class="page-item"
              :class="{ 'active': filters.page == index }"
            >
              <a class="page-link" href="#" @click.prevent="changePage(index)">{{ index }}</a>
            </li>
          </template>
          <li v-if="totalPages > (filters.page + (pageInterval / 2))" class="page-item">
            <a class="page-link" href="#" @click.prevent="nextPage()">...</a>
          </li>
          <li class="page-item" :class="{ 'disabled': filters.page >= totalPages }">
            <a class="page-link" href="#" @click.prevent="nextPage()">Siguiente</a>
          </li>
        </ul>
        <div class="data-table__page-size">
          <label class="mb-0">Mostrar</label>
          <q-select
            v-model="filters.itemsPerPage"
            :options="[{ value: 10, label: '10' }, { value: 25, label: '25' }, { value: 50, label: '50' }]"
          />
          <span>registros</span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import QSelect from '@/components/shared/QSelect.vue';
import Daterange from "../global/Daterange-component.vue";
import formatDateMixin from "../../mixins/formatDateMixin";

export default {
  components: { QSelect, Daterange },
  mixins: [formatDateMixin],
  props: [],
  data() {
    const defaultRange = this.last30DaysRange();
    return {
        orders:[],
        dateRangeStart: defaultRange.startDate,
        dateRangeEnd: defaultRange.endDate,
        filters:{
            q:'',
            oninvoiced:false,
            daterange: `${this.formatoFecha2Daterange(defaultRange.startDate)} - ${this.formatoFecha2Daterange(defaultRange.endDate)}`,
            itemsPerPage:25,
            page:1,
            sorting:{
                col:'',
                order:'desc'
            }
        },
        totalPages:1,
        pageInterval:16,
        totalItems: 0,
        openOrderId: null,
        dropdownStyle: {},
    }
  },
  mounted() {
      this.getOrders();
      document.addEventListener('click', this.handleDocumentClick);
      window.addEventListener('scroll', this.handleScrollDismiss, true);
  },
  beforeDestroy() {
      document.removeEventListener('click', this.handleDocumentClick);
      window.removeEventListener('scroll', this.handleScrollDismiss, true);
  },
  methods: {
    toggleDropdown(id, event) {
        if (this.openOrderId === id) {
            this.openOrderId = null;
            this.dropdownStyle = {};
            return;
        }
        this.openOrderId = id;
        if (event && event.currentTarget) {
            const rect = event.currentTarget.getBoundingClientRect();
            this.dropdownStyle = {
                position: 'fixed',
                top: (rect.bottom + 4) + 'px',
                right: (window.innerWidth - rect.right) + 'px',
                left: 'auto',
            };
        }
    },
    handleDocumentClick(e) {
        if (!e.target.closest('.data-table .dropdown')) {
            this.openOrderId = null;
            this.dropdownStyle = {};
        }
    },
    handleScrollDismiss() {
        if (this.openOrderId !== null) {
            this.openOrderId = null;
            this.dropdownStyle = {};
        }
    },
    getOrders(){
        var vm= this;
        this.$http.post('/api/ecom/orders/getOrders', this.filters)
          .then(response => {
            if(response.data.success) {
                vm.orders = response.data.data.list;
                vm.totalPages=response.data.data.totalPages;
                vm.totalItems=response.data.data.totalItems;
            }else{
                vm.orders=[];
                vm.totalPages=0;
            }
        })
        .catch(error => {
            alert(error);
        })
    },
    sortList(col){
        if(this.filters.sorting.col == col){
            if(this.filters.sorting.order=='asc'){
                this.filters.sorting.order='desc';
            }else{
                this.filters.sorting.order='asc';
            }
        }else{
            this.filters.sorting.col=col;
            this.filters.sorting.order='asc';
        }
        this.getOrders();
    },
    prevPage(){
        if(this.filters.page>1){
            this.filters.page = this.filters.page-1;
            this.getOrders();
        }
    },
    nextPage(){
        if (this.filters.page<this.totalPages){
            this.filters.page = this.filters.page +1;
            this.getOrders();
        }
    },
    changePage(data){
        this.filters.page = data;
        this.getOrders();
    },
    inIntervalPage(index){
        if(this.totalPages<=this.pageInterval){return true;}

        let limitprev = this.filters.page - (this.pageInterval/2);
        let limitnext = this.filters.page + (this.pageInterval/2);

        if(limitprev<0){
            limitprev=0;
            limitnext=this.pageInterval;
        }

        if(index>limitprev && index<limitnext){return true;}

        return false;
    },
    setStatus(id, status){
        var vm= this;
        let outdata={
            'id':id,
            'status':status,
        };
        if(outdata.status == 'CANCELED'){
            if(confirm("¿Está seguro que desea anular esta orden de pedido? Los cambios son irreversibles") == true){
                this.$http.post('/api/ecom/orders/updateStatus', outdata)
                .then(() => {
                    vm.getOrders();
                })
                .catch(error => {
                    alert(error);
                })
            }
        }else{
            this.$http.post('/api/ecom/orders/updateStatus', outdata)
            .then(() => {
                vm.getOrders();
            })
            .catch(error => {
                alert(error);
            })
        }
    },
    deleteOrder(id){
        var vm=this;
        this.$http.get('/api/ecom/orders/deleteOrder/' + id)
          .then(() => {
            vm.getOrders();
          })
          .catch(error => {
            alert(error);
          })
    },
    seeDetail(id){
        window.location.href = '/ecom/order/' + id;
    },
    onDatePicked(date){
        this.dateRangeStart = date.startDate;
        this.dateRangeEnd = date.endDate;
        this.filters.daterange = (date.startDate && date.endDate)
          ? `${this.formatoFecha2Daterange(date.startDate)} - ${this.formatoFecha2Daterange(date.endDate)}`
          : null;
    },
    clearFilters(){
        this.filters.q = '';
        this.filters.oninvoiced = false;
        const defaultRange = this.last30DaysRange();
        this.onDatePicked(defaultRange);
    },
  },
  watch: {
      'filters.q': {handler: function () {this.getOrders();}},
      'filters.oninvoiced': {handler: function () {this.getOrders();}},
      'filters.itemsPerPage': {handler: function () {this.getOrders();}},
      'filters.daterange': {handler: function () {this.getOrders();}},
  },
}
</script>

<style scoped>
.filters-panel__field--inline {
  display: flex;
  align-items: center;
  gap: 4px;
}

.filters-panel__field--inline .control-label {
  margin: 0;
  white-space: nowrap;
  font-size: 13px;
  color: var(--text);
}

.filters-panel__field--inline > .form-group {
  flex: 1 1 auto;
  margin-bottom: 0;
}
</style>


